/* ---------------------------------------------------------------------------
   Seed register — DPWH, Daang Maharlika Road Improvement Project.

   PROVENANCE — READ THIS BEFORE TRUSTING A NUMBER, AND READ THE PARAGRAPH ON
   COUNTS BEFORE QUOTING A TOTAL.

   Every record below comes from one source: the DPWH Daang Maharlika Road
   Improvement Project market-sounding pack of March 2026, as reproduced in the
   `bridge_watch.html` prototype that accompanied it. That source lists SIX
   things per structure and no more — bridge ID, name, contract package, station,
   total length and lane count. Everything else a bridge-inventory record can
   hold is therefore null here: no coordinates, no spans, no deck or carriageway
   width, no year of completion, no watercourse, no superstructure type, no
   foundation, no bearings, no design load. Those are NOT omissions to be filled
   in with something plausible. Nobody has surveyed these structures, and a
   register that guessed would be a register nobody can tell apart from one that
   knows.

   NOTHING HERE HAS BEEN INSPECTED. `inspections` is empty on all 108 records,
   which is the source's own position — the pack states that the condition survey
   for the two priority packages is a contractual deliverable falling in month
   3–4 of construction, and that field inspection has not commenced. So the
   register shows every structure as never inspected, and it is right to.

   THE CONDITION AGAINST EACH STRUCTURE IS A DESK ASSESSMENT, NOT A RATING. It is
   carried as the `assessedCondition` extra field and nowhere else. It does not
   feed the element condition ratings, it does not produce a condition band on
   the network summary, and it must never be quoted as though it did — see the
   note on that field in src/tenant/profiles/ph-dpwh.ts. The register's own
   condition figures stay empty until somebody inspects something, which is the
   whole difference between this file and a survey.

   THE STATION IS THE FIGURE OF RECORD; `km` IS DERIVED FROM IT. Two of the
   stations in the pack are not normalised — `848+1066` is 1066 m past kilometre
   848, and `255+1166` likewise — so writing `km` back out as a station would
   silently correct them to `849+066` and `256+166`. The number is what the map
   and the sort use; the string is what the screen shows beside a printed plan.
   See `stationOf` in src/tenant/format.ts.

   THE SOURCE DOES NOT AGREE WITH ITSELF ABOUT HOW MANY BRIDGES THERE ARE, and
   this file does not resolve the disagreement. Its headline, its dashboard tile
   and its section cards all say 106 — 34 in Samar, 50 in Agusan del Sur, 22 on
   Quirino-Andaya. Its actual list holds 108 rows with 108 distinct bridge IDs,
   and the Agusan packages account for 52 of them rather than 50. The list is
   what is transcribed below, because a list of named structures with distinct
   asset IDs is stronger evidence than a total in a heading, and because dropping
   two real records to make a headline add up is the one thing a register must
   not do. Whoever next speaks to DPWH should ask which is right; until then the
   register says 108, and this paragraph says why.

   ONE RECORD HAS NO LENGTH. Simulao (B00928MN) is listed at 0.00 m, which the
   prototype itself renders as a dash — so zero is that source's way of writing
   *not recorded*, and it is stored here as null rather than as a bridge of no
   length.

   DISTRICTS ARE THE DISTRICT ENGINEERING OFFICE'S, and the B packages
   have none. The pack labels the Samar and Agusan packages with a DEO district
   and labels the Quirino-Andaya packages with a highway name instead. So
   `district` is null on all 22 B-package records, and `province` is null on all
   108 — the pack names sections, not provinces, and Quirino-Andaya spans more
   than one province.
   --------------------------------------------------------------------------- */

import type { Bridge } from './types.ts';
import { rbiFor } from './dpwh-rbi.ts';

/**
 * One structure exactly as the market-sounding pack lists it.
 *
 * A tuple rather than an object literal per record, so 108 rows fit on 108 lines
 * and a transcription error shows up as a misaligned column. Column order is the
 * pack's own: id, name, package, station, length in metres (null where the pack
 * writes zero), lane count, assessed condition.
 */
type SourceRow = readonly [
  id: string,
  name: string,
  pkg: string,
  station: string,
  lengthM: number | null,
  lanes: number,
  assessed: string,
];

/**
 * What the pack calls each contract package, and where it sits.
 *
 * `district` is the District Engineering Office's and is null for the
 * three Quirino-Andaya packages, which the pack labels by highway instead — see
 * the header. `road` is the highway name; the pack carries no DPWH route code
 * for any of these, which is why every record's `route` below is null and why the
 * DPWH profile classifies no road codes at all.
 */
interface PackageInfo {
  section: string;
  road: string;
  district: string | null;
}

const PACKAGES: Readonly<Record<string, PackageInfo>> = {
  S1: { section: 'Samar', road: 'Maharlika Highway', district: 'Samar 2nd District' },
  S2: { section: 'Samar', road: 'Maharlika Highway', district: 'Samar 2nd District' },
  S3: { section: 'Samar', road: 'Maharlika Highway', district: 'Samar 2nd District' },
  S4: { section: 'Samar', road: 'Maharlika Highway', district: 'Samar 2nd District' },
  A1: { section: 'Agusan del Sur', road: 'Maharlika Highway', district: 'Agusan del Sur 1st District' },
  A2: { section: 'Agusan del Sur', road: 'Maharlika Highway', district: 'Agusan del Sur 1st District' },
  A3: { section: 'Agusan del Sur', road: 'Maharlika Highway', district: 'Agusan del Sur 2nd District' },
  A4: { section: 'Agusan del Sur', road: 'Maharlika Highway', district: 'Agusan del Sur 2nd District' },
  A5: { section: 'Agusan del Sur', road: 'Maharlika Highway', district: 'Agusan del Sur 2nd District' },
  B1: { section: 'Quirino-Andaya', road: 'Quirino Highway', district: null },
  B2: { section: 'Quirino-Andaya', road: 'Andaya Highway', district: null },
  B3: { section: 'Quirino-Andaya', road: 'Andaya Highway', district: null },
};

/**
 * How the pack writes an assessed condition, and how this register says it.
 *
 * `uninspected` becomes null rather than the words "not inspected": the absence
 * of an assessment is the same absence every other unrecorded field has, and
 * giving it a label of its own would put a value in a column whose whole meaning
 * is that there is no value. The four that ARE assessments keep the pack's own
 * words, capitalised as a heading would capitalise them.
 */
const ASSESSED: Readonly<Record<string, string | null>> = {
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
  critical: 'Critical',
  uninspected: null,
};

/**
 * A DPWH station as kilometres.
 *
 * `796+180` is 796 km and 180 m. The metres part is NOT assumed to be under a
 * thousand — the pack contains `848+1066`, and treating a four-digit remainder
 * as an error would either drop a record or move a bridge a kilometre. So the
 * arithmetic is simply kilometres plus metres, whatever the metres are, and the
 * original string is kept beside it as the figure of record.
 *
 * Throws on anything that is not `<digits>+<digits>`. A station that cannot be
 * parsed is a transcription fault in this file, and the register would otherwise
 * carry a structure at kilometre NaN — which sorts unpredictably and plots
 * nowhere, with nothing on screen to say why.
 */
export function parseStation(station: string): number {
  const m = /^(\d+)\+(\d+)$/.exec(station.trim());
  if (!m) throw new Error(`Unparseable DPWH station: "${station}"`);
  return Number(m[1]) + Number(m[2]) / 1000;
}

/** Every field this register has no source for. Spread into each record. */
const UNRECORDED = {
  route: null,
  province: null,
  river: null,
  type: null,
  spans: null,
  deckWidth: null,
  carriageway: null,
  year: null,
  deckRL: null,
  hfl: null,
  lat: null,
  lng: null,
  deckBearing: null,
  inventoryCode: null,
  roadLength: null,
  divisionOffice: null,
  overUnder: null,
  loadRestriction: null,
  heightRestriction: null,
  designLoad: null,
  leftBankDistrict: null,
  leftBankVdc: null,
  rightBankDistrict: null,
  rightBankVdc: null,
  spanLength: null,
  abutmentType: null,
  abutmentFoundation: null,
  pierType: null,
  pierFoundation: null,
  bearings: null,
  railings: null,
  movement: null,
  riverTraining: null,
  runningSurface: null,
  roadSigns: null,
  services: null,
  designedBy: null,
  constructedBy: null,
  aadt: null,
  aadtYear: null,
  detourKm: null,
  detourMinutes: null,
  lifeline: null,
} as const;

const SOURCE_ROWS: readonly SourceRow[] = [
  /* --- S1 --- */
  ['B00373SM',  'Pupua',                  'S1',  '796+180',   28.00,   4,  'poor'],
  ['B00555SM',  'Antiao (New)',           'S1',  '801+871',   66.00,   2,  'critical'],
  ['B00375SM',  'First Br. 3',            'S1',  '802+880',   6.33,    2,  'fair'],
  ['B00376SM',  'Jiabong',                'S1',  '813+120',   72.00,   2,  'poor'],
  /* --- S2 --- */
  ['B00377SM',  'Motiong',                'S2',  '820+893',   26.00,   4,  'fair'],
  ['B00379SM',  'Bato',                   'S2',  '829+632',   16.40,   4,  'poor'],
  ['B00380SM',  'Yabon',                  'S2',  '837+297',   16.00,   2,  'fair'],
  ['B00381SM',  'Tinago',                 'S2',  '837+411',   10.55,   2,  'uninspected'],
  ['B00382SM',  'Rawis',                  'S2',  '837+609',   24.65,   2,  'poor'],
  ['B00383SM',  'Bucalan',                'S2',  '839+786',   31.60,   2,  'uninspected'],
  ['B00384SM',  'Balugo',                 'S2',  '841+713',   38.55,   4,  'fair'],
  /* --- S3 --- */
  ['B00385SM',  'Cabugao',                'S3',  '846+814',   35.35,   4,  'fair'],
  ['B00386SM',  'Polangi',                'S3',  '848+545',   28.35,   4,  'poor'],
  ['B00387SM',  'Calbiga',                'S3',  '848+1066',  105.78,  2,  'fair'],
  ['B00388SM',  'Calbiga Overpass',       'S3',  '849+008',   14.00,   2,  'good'],
  ['B00389SM',  'Obayan Br. 1',           'S3',  '851+943',   6.00,    2,  'uninspected'],
  ['B00390SM',  'Obayan Br. 2',           'S3',  '852+015',   6.00,    2,  'uninspected'],
  ['B00391SM',  'Botoc',                  'S3',  '853+520',   16.00,   4,  'poor'],
  ['B00392SM',  'Camandang',              'S3',  '855+083',   18.00,   4,  'fair'],
  ['B00393SM',  'Libunao',                'S3',  '857+053',   25.35,   4,  'poor'],
  ['B00394SM',  'Lale',                   'S3',  '857+638',   25.00,   4,  'good'],
  ['B00395SM',  'Antol',                  'S3',  '860+698',   28.00,   4,  'fair'],
  ['B00396SM',  'Parasanon',              'S3',  '865+779',   25.40,   4,  'poor'],
  ['B00397SM',  'Silaga',                 'S3',  '871+459',   56.45,   4,  'critical'],
  ['B00398SM',  'Manunca',                'S3',  '874+909',   24.00,   4,  'uninspected'],
  /* --- S4 --- */
  ['B00399SM',  'Lupig',                  'S4',  '876+008',   20.73,   4,  'poor'],
  ['B00400SM',  'Catungan',               'S4',  '877+562',   16.00,   4,  'fair'],
  ['B00401SM',  'Dalid',                  'S4',  '879+180',   22.75,   4,  'poor'],
  ['B00402SM',  'Apologon',               'S4',  '880+515',   18.00,   4,  'uninspected'],
  ['B00403SM',  'Tinabogan',              'S4',  '885+177',   31.76,   4,  'poor'],
  ['B00404SM',  'Alal-on Br. 1',          'S4',  '885+521',   30.00,   4,  'fair'],
  ['B00405SM',  'Alal-on Br. 2',          'S4',  '886+036',   21.00,   4,  'uninspected'],
  ['B00406SM',  'Alal-on Br. 3',          'S4',  '886+719',   16.00,   4,  'poor'],
  ['B00407SM',  'Alal-on Br. 4',          'S4',  '886+846',   15.95,   4,  'uninspected'],
  /* --- A1 --- */
  ['B00894MN',  'Afga',                   'A1',  '1246+389',  24.15,   4,  'good'],
  ['B00895MN',  'Hupas',                  'A1',  '1255+188',  35.50,   4,  'fair'],
  ['B01982MN',  'Wawa Parallel',          'A1',  '1257+481',  240.40,  2,  'poor'],
  ['B00897MN',  'Bayanacon',              'A1',  '1259+466',  55.80,   2,  'fair'],
  ['B00898MN',  'Mambutay',               'A1',  '1262+462',  21.65,   4,  'uninspected'],
  ['B00900MN',  'Andanan',                'A1',  '1266+105',  179.45,  2,  'critical'],
  ['B02246MN',  'Andanan Parallel',       'A1',  '1266+105',  189.00,  2,  'poor'],
  ['B00901MN',  'Taglatawan',             'A1',  '1269+794',  22.10,   4,  'fair'],
  /* --- A2 --- */
  ['B00902MN',  'Sinadjap',               'A2',  '1271+580',  48.45,   4,  'good'],
  ['B00903MN',  'Bayugan Relief',         'A2',  '1273+933',  19.95,   4,  'fair'],
  ['B00904MN',  'Bayugan Br. 1',          'A2',  '1274+000',  59.80,   4,  'poor'],
  ['B00905MN',  'Maytibog',               'A2',  '1274+815',  26.79,   4,  'uninspected'],
  ['B00906MN',  'Labao',                  'A2',  '1277+419',  19.20,   2,  'fair'],
  ['B00907MN',  'Panaytay',               'A2',  '1278+569',  31.30,   4,  'poor'],
  ['B00908MN',  'Sianib',                 'A2',  '1280+805',  52.55,   2,  'critical'],
  ['B00909MN',  'Awa',                    'A2',  '1289+935',  41.55,   4,  'fair'],
  ['B00910MN',  'Gibong',                 'A2',  '1294+035',  125.01,  2,  'poor'],
  ['B02247MN',  'Gibong Parallel',        'A2',  '1294+035',  125.00,  2,  'uninspected'],
  ['B01739MN',  'Patin-ay Br. 1',         'A2',  '1301+438',  20.10,   4,  'fair'],
  ['B01740MN',  'Patin-ay Br. 2',         'A2',  '1302+253',  24.40,   4,  'good'],
  /* --- A3 --- */
  ['B01744MN',  'Hubang',                 'A3',  '1307+493',  22.00,   4,  'fair'],
  ['B01798MN',  'Adlayan',                'A3',  '1307+584',  30.30,   4,  'poor'],
  ['B00915MN',  'Tagconayon',             'A3',  '1314+517',  24.30,   2,  'uninspected'],
  ['B01939MN',  'Tagconayon Parallel',    'A3',  '1314+517',  24.10,   2,  'uninspected'],
  ['B00916MN',  'Bayugan Br. 2',          'A3',  '1314+951',  21.90,   4,  'fair'],
  ['B00917MN',  'Anagasian',              'A3',  '1317+705',  34.06,   2,  'poor'],
  ['B01964MN',  'Anagasian Parallel',     'A3',  '1317+708',  37.00,   2,  'uninspected'],
  ['B00918MN',  'Lagcogangan',            'A3',  '1320+566',  22.05,   2,  'good'],
  ['B01940MN',  'Lagcogangan Parallel',   'A3',  '1320+567',  23.40,   2,  'fair'],
  ['B00919MN',  'Tagbayagan',             'A3',  '1323+414',  26.10,   4,  'poor'],
  ['B00899MN',  'Solibao',                'A3',  '1325+239',  75.50,   2,  'critical'],
  ['B01969MN',  'Solibao Parallel',       'A3',  '1325+239',  76.80,   2,  'poor'],
  ['B00920MN',  'Padigusan',              'A3',  '1329+542',  24.50,   2,  'uninspected'],
  ['B01941MN',  'Padigusan Parallel',     'A3',  '1329+542',  26.10,   2,  'uninspected'],
  ['B00921MN',  'Lahi',                   'A3',  '1330+558',  27.85,   4,  'fair'],
  ['B00922MN',  'Wasian',                 'A3',  '1330+958',  26.40,   4,  'poor'],
  ['B00923MN',  'Boan',                   'A3',  '1331+781',  15.12,   4,  'uninspected'],
  /* --- A4 --- */
  ['B00924MN',  'Bayugan Br. 3',          'A4',  '1334+157',  21.95,   2,  'fair'],
  ['B01942MN',  'Bayugan Parallel 3',     'A4',  '1334+157',  22.00,   2,  'poor'],
  ['B00925MN',  'Koo',                    'A4',  '1341+732',  27.30,   2,  'uninspected'],
  ['B01943MN',  'Koo Parallel',           'A4',  '1341+732',  28.00,   2,  'uninspected'],
  ['B00926MN',  'Singanan',               'A4',  '1343+629',  24.80,   2,  'fair'],
  ['B01979MN',  'Singanan Parallel',      'A4',  '1343+629',  24.84,   2,  'poor'],
  ['B00927MN',  'Bunawan',                'A4',  '1351+961',  73.32,   2,  'critical'],
  ['B01965MN',  'Bunawan Parallel',       'A4',  '1351+961',  73.00,   2,  'poor'],
  ['B00928MN',  'Simulao',                'A4',  '1355+171',  null,    2,  'uninspected'],
  /* --- A5 --- */
  ['B00929MN',  'Magalibobo',             'A5',  '1372+621',  26.10,   2,  'fair'],
  ['B01967MN',  'Magalibobo Parallel',    'A5',  '1372+621',  26.80,   2,  'poor'],
  ['B00930MN',  'Onoman',                 'A5',  '1374+000',  24.00,   2,  'uninspected'],
  ['B01968MN',  'Onoman Parallel',        'A5',  '1374+000',  24.80,   2,  'uninspected'],
  ['B00931MN',  'Langkilaan',             'A5',  '1376+880',  41.50,   2,  'poor'],
  ['B01966MN',  'Langkilaan Parallel',    'A5',  '1376+880',  40.80,   2,  'fair'],
  /* --- B1 --- */
  ['B02595LZ',  'Gumihan',                'B1',  '251+337',   23.00,   2,  'poor'],
  ['B02596LZ',  'Umiik',                  'B1',  '255+1166',  16.00,   2,  'critical'],
  ['B02597LZ',  'Kabibihan',              'B1',  '256+207',   60.00,   2,  'poor'],
  ['B02598LZ',  'Bolo 2',                 'B1',  '258+655',   24.00,   2,  'fair'],
  ['B02599LZ',  'Kinatakutan',            'B1',  '264+400',   31.00,   2,  'critical'],
  /* --- B2 --- */
  ['B00447LZ',  'Cabatbatan',             'B2',  '285+888',   27.00,   4,  'fair'],
  ['B00446LZ',  'Kilbay',                 'B2',  '288+375',   136.00,  4,  'poor'],
  ['B00445LZ',  'Catabangan',             'B2',  '297+560',   20.00,   4,  'uninspected'],
  ['B00444LZ',  'Malabog',                'B2',  '302+255',   27.00,   4,  'poor'],
  ['B00443LZ',  'Paculago Br. 1',         'B2',  '303+955',   81.00,   4,  'uninspected'],
  ['B00442LZ',  'Paculago Br. 2',         'B2',  '304+917',   22.00,   4,  'fair'],
  ['B00441LZ',  'Kanagnagan',             'B2',  '308+241',   28.00,   4,  'poor'],
  ['B00440LZ',  'Binahan',                'B2',  '310+689',   30.00,   4,  'uninspected'],
  /* --- B3 --- */
  ['B00439LZ',  'Hidong',                 'B3',  '311+223',   36.10,   2,  'critical'],
  ['B00438LZ',  'Panaytayan',             'B3',  '314+207',   28.00,   4,  'poor'],
  ['B00437LZ',  'Ragay',                  'B3',  '316+915',   100.00,  2,  'critical'],
  ['B00436LZ',  'Pangaluan',              'B3',  '320+961',   30.10,   4,  'uninspected'],
  ['B00435LZ',  'PNR Overpass',           'B3',  '330+649',   22.30,   2,  'poor'],
  ['B00434LZ',  'Mauca',                  'B3',  '330+740',   82.00,   2,  'fair'],
  ['B00433LZ',  'Alayao',                 'B3',  '333+948',   15.50,   4,  'uninspected'],
  ['B00432LZ',  'Manangle',               'B3',  '335+038',   24.00,   4,  'good'],
  ['B00431LZ',  'Malangog',               'B3',  '339+533',   31.10,   4,  'poor'],];

/**
 * One source row as a register record.
 *
 * Exported so the seed-SQL generator and the tests build records the same way
 * this module does, rather than each repeating the mapping — which is where the
 * station-to-kilometre derivation would eventually drift.
 */
export function toDpwhBridge(row: SourceRow): Bridge {
  const [id, name, pkg, station, lengthM, lanes, assessed] = row;
  const info = PACKAGES[pkg];
  if (!info) throw new Error(`Unknown DPWH contract package "${pkg}" on ${id}`);

  /* THE SECOND SOURCE, joined here and nowhere else. 43 of the 108 have a match
     in DPWH's own Road & Bridge Inventory by way of OpenStreetMap, on this
     register's bridge id — see the header of src/data/dpwh-rbi.ts, which is
     where the provenance and the five corroborating checks are written down. The
     other 65 get null and stay null. */
  const rbi = rbiFor(id);

  return {
    ...UNRECORDED,
    id,
    name,
    roadName: info.road,
    km: parseStation(station),

    /* THE PACK'S LENGTH, ALWAYS. The inventory carries its own and they agree to
       within rounding on 34 of 40 — but PNR Overpass disagrees by 17 m, and
       quietly preferring one sourced figure over another is how a register stops
       being auditable. The other number is kept in dpwh-rbi.ts so the
       disagreement stays visible. */
    length: lengthM,

    /* Inventory values where there is a match, and the pack's own where there is
       not. `district` is the one field both sources reach: the pack labels the
       Samar and Agusan packages by District Engineering Office and the
       Quirino-Andaya ones by highway, and the inventory fills those 22 in —
       Quezon 4th for B1, Camarines Sur 1st for B2 and B3, which is why the pack
       could not label them with one office. Where both have it they agree, on
       all 24 records that carry it. */
    lat: rbi?.lat ?? null,
    lng: rbi?.lng ?? null,
    deckWidth: rbi?.deckWidth ?? null,
    year: rbi?.year ?? null,
    type: rbi?.type ?? null,
    district: rbi?.district ?? info.district,
    province: rbi?.province ?? null,

    tenantFields: {
      package: pkg,
      section: info.section,
      station,
      lanes,
      assessedCondition: ASSESSED[assessed] ?? null,
      /* The barangay and the municipality the inventory names. In the extras bag
         rather than `leftBankVdc`/`rightBankVdc`, because those two are
         bank-specific on a DoR sheet and the inventory gives one locality for
         the structure rather than one per bank. */
      barangay: rbi?.barangay ?? null,
      municipality: rbi?.municipality ?? null,
    },
    inspections: [],
  };
}

/**
 * The bundled DPWH register, in the order the pack prints.
 *
 * Package order, and within a package increasing station — which is also the
 * order somebody driving the corridor meets them in. `sort_order` in D1
 * preserves it; nothing downstream re-sorts by default.
 */
export const BRIDGES_DPWH: readonly Bridge[] = SOURCE_ROWS.map(toDpwhBridge);

/** Every contract package the register carries, in the order the pack lists them. */
export const DPWH_PACKAGES: readonly string[] = Object.keys(PACKAGES);

/** What one package is: its section, its highway, and its DEO district if it has one. */
export function packageInfo(code: string): PackageInfo | null {
  return PACKAGES[code] ?? null;
}
